/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { FC, createContext, useContext, useMemo, ReactNode } from 'react';

export interface HomepageProviderConfig {
  forcedPlatform?: 'mobile' | 'web' | 'web-mobile';
}

interface HomepageContextType {
  forcedPlatform?: 'mobile' | 'web' | 'web-mobile';
}

const HomepageContext = createContext<HomepageContextType | undefined>(
  undefined,
);

export const HomepageProvider: FC<{
  children: ReactNode;
  config?: HomepageProviderConfig;
}> = ({ children, config = {} }) => {
  // Memoize context to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      forcedPlatform: config.forcedPlatform,
    }),
    [config.forcedPlatform],
  );

  return (
    <HomepageContext.Provider value={contextValue}>
      {children}
    </HomepageContext.Provider>
  );
};

export const useHomepageContext = () => {
  const context = useContext(HomepageContext);
  if (context === undefined) {
    throw new Error(
      'useHomepageContext must be used within a HomepageProvider',
    );
  }
  return context;
};

export const useForcedPlatform = () => {
  return useHomepageContext().forcedPlatform;
};

export const useOptionalForcedPlatform = () => {
  return useContext(HomepageContext)?.forcedPlatform;
};
